/**
 * Authentication.
 *
 * Everything here goes through the versioned prefix except the two Endeavor X endpoints, which
 * the API registers at the root — `EX_REDIRECT_URI` is already registered with Endeavor X's OAuth
 * client, so a prefix would mean re-registering it. {@link ssoLoginHref} builds that URL for an
 * anchor rather than for a `fetch`: SSO is a full-page redirect, and a `fetch` would follow it to
 * the identity provider inside an XHR and fail CORS.
 */
import {
  authConfigResponseSchema,
  invitePreviewResponseSchema,
  meResponseSchema,
  okResponseSchema,
  type AcceptInviteRequest,
  type AuthConfigResponse,
  type AuthUserDto,
  type InvitePreviewResponse,
  type LoginRequest,
} from '@catalog/shared';

import { apiFetch, isUnauthenticated } from './client.js';
import { queryKeys, STALE_SESSION } from './query-keys.js';
import { webEnv } from '../env.js';

/**
 * Whether to render the "Sign in with Endeavor X" button at all.
 *
 * Cached for the session: it is decided by the API's boot-time configuration and cannot change
 * while the page is open.
 */
export function authConfigQuery() {
  return {
    queryKey: queryKeys.authConfig(),
    queryFn: async ({ signal }: { signal: AbortSignal }): Promise<AuthConfigResponse> =>
      apiFetch('/auth/config', { schema: authConfigResponseSchema, signal }),
    staleTime: Number.POSITIVE_INFINITY,
  };
}

/**
 * The current user, or `null` when nobody is signed in.
 *
 * A 401 is the *answer* here, not a failure — most of this app is readable anonymously — so it
 * resolves to `null` and is explicitly exempted from the global 401 redirect. Any other failure
 * still rejects, because "the service is down" and "you are signed out" must not render the same.
 *
 * `retry: false` because the one retryable-looking case is exactly the one already handled.
 */
export function meQuery() {
  return {
    queryKey: queryKeys.me(),
    queryFn: async ({ signal }: { signal: AbortSignal }): Promise<AuthUserDto | null> => {
      try {
        const response = await apiFetch('/auth/me', {
          schema: meResponseSchema,
          signal,
          ignoreUnauthorized: true,
        });

        return response.user;
      } catch (error) {
        if (isUnauthenticated(error)) return null;
        throw error;
      }
    },
    staleTime: STALE_SESSION,
    retry: false,
  };
}

export async function login(body: LoginRequest): Promise<AuthUserDto> {
  const response = await apiFetch('/auth/login', {
    schema: meResponseSchema,
    method: 'POST',
    body,
  });

  return response.user;
}

export async function logout(): Promise<void> {
  await apiFetch('/auth/logout', { schema: okResponseSchema, method: 'POST' });
}

/**
 * Validates an invitation token and returns the invited address.
 *
 * Unknown, expired and already-accepted tokens are one indistinguishable 404 by design, so the
 * screen can only ever say "this invitation is no longer valid" — which is all it should say.
 */
export function invitePreviewQuery(token: string) {
  return {
    queryKey: queryKeys.invite(token),
    queryFn: async ({ signal }: { signal: AbortSignal }): Promise<InvitePreviewResponse> =>
      apiFetch(`/auth/invites/${encodeURIComponent(token)}`, {
        schema: invitePreviewResponseSchema,
        signal,
      }),
    retry: false,
    staleTime: STALE_SESSION,
  };
}

export async function acceptInvite(token: string, body: AcceptInviteRequest): Promise<AuthUserDto> {
  const response = await apiFetch(`/auth/invites/${encodeURIComponent(token)}/accept`, {
    schema: meResponseSchema,
    method: 'POST',
    body,
  });

  return response.user;
}

/**
 * The href for the SSO button.
 *
 * `returnTo` is sent as a query parameter and validated server-side against an allowlist of
 * internal paths — an absolute or protocol-relative URL is a 400 there, which is the right place
 * for that check to live.
 */
export function ssoLoginHref(ssoLoginPath: string, returnTo?: string): string {
  const base = `${webEnv.apiBaseUrl}${ssoLoginPath}`;
  if (returnTo === undefined || returnTo.length === 0) return base;

  return `${base}?returnTo=${encodeURIComponent(returnTo)}`;
}
